import type { ReviewKind } from '../../../shared/progress';
import { BRIEF_TYPED_SUGGESTION, type SceneVars } from '../../lib/tourScenes';
import ReviewPageChrome, {
  BRIEF_AUDIENCE_BODY,
  BRIEF_AUDIENCE_TITLE,
  BRIEF_PURPOSE_BODY,
  BRIEF_PURPOSE_TITLE,
  BriefDocPanel,
  DocPanel,
} from './PageChrome';

const TYPED_SUGGESTION = ' The city wakes up with us.';

/**
 * suggest-edits stage — ported 1:1 from mockups/tour-refinement-2.html:
 * page A (review, before) -> page B (suggest mode, editable doc) -> page C
 * (review, after, with the client's addition shown in teal). Pages are
 * absolutely stacked and opacity-crossfaded, matching the brand's fade-only
 * page-transition rule.
 */
export default function SuggestEditsStage({ vars, kind }: { vars: SceneVars; kind: ReviewKind }) {
  if (kind === 'brief') return <BriefSuggestEditsStage vars={vars} />;
  return <ScriptSuggestEditsStage vars={vars} />;
}

function ScriptSuggestEditsStage({ vars }: { vars: SceneVars }) {
  const page = vars.page as string;
  const typed = (vars.typed as string) ?? '';
  const editingCell = Boolean(vars.editingCell);
  const saveState = vars.saveState as 'idle' | 'saving' | 'saved';
  const bannerLit = Boolean(vars.bannerLit);
  const noticeShown = Boolean(vars.noticeShown);

  return (
    <>
      <Page visible={page === 'A'}>
        <ReviewPageChrome kind="script" card={{ suggestLit: Boolean(vars.suggestActionLit) }}>
          <DocPanel
            top={345}
            rows={[
              {
                audio: 'Every morning starts the same way: the first pour.',
                visual: 'Steam over the machine, dawn light.',
              },
              {
                audio: 'Aurora roasts in small batches every week.',
                visual: 'Hands tipping green beans into the roaster.',
              },
            ]}
          />
        </ReviewPageChrome>
      </Page>

      <Page visible={page === 'B'}>
        <div className="absolute inset-0 bg-page">
          <div
            className={`h-14 flex items-center justify-between px-9 bg-accent/[0.07] border-b border-accent transition-shadow duration-300 ${
              bannerLit ? 'shadow-[inset_0_0_0_2px_var(--color-accent)]' : ''
            }`}
          >
            <span className="flex items-center gap-4 min-w-0">
              <span className="font-mono text-[11px] uppercase tracking-[0.12em] font-semibold text-accent whitespace-nowrap">
                // Suggest mode
              </span>
              <span className="text-[13px] text-muted whitespace-nowrap overflow-hidden text-ellipsis">
                Edit the script directly. Your team can review and send it together.
              </span>
            </span>
            <span className="flex items-center gap-3 shrink-0">
              <SaveChip state={saveState} />
              <span className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-accent text-accent-fg font-semibold text-[12px] whitespace-nowrap">
                Done editing
              </span>
            </span>
          </div>
          <DocPanel
            top={80}
            rows={[
              {
                editing: editingCell,
                audio: (
                  <>
                    Every morning starts the same way: the first pour.
                    <span className="text-accent-hover">{typed}</span>
                    {editingCell && typed.length < TYPED_SUGGESTION.length && <Caret />}
                  </>
                ),
                visual: 'Steam over the machine, dawn light.',
              },
              {
                audio: 'Aurora roasts in small batches every week.',
                visual: 'Hands tipping green beans into the roaster.',
              },
              {
                audio: 'From the roaster to your cup in seventy-two hours.',
                visual: 'Timelapse: roast, rest, grind, brew.',
              },
            ]}
          />
        </div>
      </Page>

      <Page visible={page === 'C'}>
        <ReviewPageChrome
          kind="script"
          card={{ resumeMode: true, suggestLit: Boolean(vars.resumeActionLit) }}
          notice={
            noticeShown
              ? "You're viewing your team's edited draft. Resume editing to keep going."
              : null
          }
        >
          <DocPanel
            top={noticeShown ? 380 : 345}
            rows={[
              {
                audio: (
                  <>
                    Every morning starts the same way: the first pour.
                    <span className="text-accent-hover">{TYPED_SUGGESTION}</span>
                  </>
                ),
                visual: 'Steam over the machine, dawn light.',
              },
              {
                audio: 'Aurora roasts in small batches every week.',
                visual: 'Hands tipping green beans into the roaster.',
              },
            ]}
          />
        </ReviewPageChrome>
      </Page>
    </>
  );
}

function Page({ visible, children }: { visible: boolean; children: React.ReactNode }) {
  return (
    <div
      className={`absolute inset-0 transition-opacity duration-[450ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
        visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      {children}
    </div>
  );
}

function Caret() {
  return <span className="inline-block w-[2px] h-[1.05em] bg-accent align-text-bottom ml-px animate-pulse" aria-hidden />;
}

function SaveChip({ state }: { state: 'idle' | 'saving' | 'saved' }) {
  if (state === 'idle') return null;
  const saved = state === 'saved';
  return (
    <span
      className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.08em] font-semibold whitespace-nowrap ${
        saved ? 'text-success' : 'text-faint'
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${saved ? 'bg-success' : 'bg-accent animate-pulse'}`} aria-hidden />
      {saved ? 'Saved' : 'Saving'}
    </span>
  );
}

/**
 * The same three pages on an interview brief: titled sections instead of
 * AUDIO / VISUAL rows, and the client's addition lands at the end of the
 * Audience paragraph. `data-tour-target` marks what the dev harness measures
 * to derive this scene's cursor coordinates (preview-scenes.html, src/dev/previewScenes.tsx).
 */
function BriefSuggestEditsStage({ vars }: { vars: SceneVars }) {
  const page = vars.page as string;
  const typed = (vars.typed as string) ?? '';
  const editingCell = Boolean(vars.editingCell);
  const saveState = vars.saveState as 'idle' | 'saving' | 'saved';
  const bannerLit = Boolean(vars.bannerLit);
  const noticeShown = Boolean(vars.noticeShown);

  return (
    <>
      <Page visible={page === 'A'}>
        <ReviewPageChrome kind="brief" card={{ suggestLit: Boolean(vars.suggestActionLit) }}>
          <BriefDocPanel
            top={345}
            sections={[
              { title: BRIEF_PURPOSE_TITLE, paragraphs: [BRIEF_PURPOSE_BODY] },
              { title: BRIEF_AUDIENCE_TITLE, paragraphs: [BRIEF_AUDIENCE_BODY] },
            ]}
          />
        </ReviewPageChrome>
      </Page>

      <Page visible={page === 'B'}>
        <div className="absolute inset-0 bg-page">
          <div
            className={`h-14 flex items-center justify-between px-9 bg-accent/[0.07] border-b border-accent transition-shadow duration-300 ${
              bannerLit ? 'shadow-[inset_0_0_0_2px_var(--color-accent)]' : ''
            }`}
          >
            <span className="flex items-center gap-4 min-w-0">
              <span className="font-mono text-[11px] uppercase tracking-[0.12em] font-semibold text-accent whitespace-nowrap">
                // Suggest mode
              </span>
              <span className="text-[13px] text-muted whitespace-nowrap overflow-hidden text-ellipsis">
                Edit the brief directly. Your team can review and send it together.
              </span>
            </span>
            <span className="flex items-center gap-3 shrink-0">
              <SaveChip state={saveState} />
              <span
                data-tour-target="done-editing"
                className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-accent text-accent-fg font-semibold text-[12px] whitespace-nowrap"
              >
                Done editing
              </span>
            </span>
          </div>
          <BriefDocPanel
            top={80}
            sections={[
              { title: BRIEF_PURPOSE_TITLE, paragraphs: [BRIEF_PURPOSE_BODY] },
              {
                title: BRIEF_AUDIENCE_TITLE,
                editing: editingCell,
                target: 'edit-para',
                paragraphs: [
                  <>
                    {BRIEF_AUDIENCE_BODY}
                    <span className="text-accent-hover">{typed}</span>
                    {editingCell && typed.length < BRIEF_TYPED_SUGGESTION.length && <Caret />}
                  </>,
                ],
              },
            ]}
          />
        </div>
      </Page>

      <Page visible={page === 'C'}>
        <ReviewPageChrome
          kind="brief"
          card={{ resumeMode: true, suggestLit: Boolean(vars.resumeActionLit) }}
          notice={
            noticeShown
              ? "You're viewing your team's edited draft. Resume editing to keep going."
              : null
          }
        >
          <BriefDocPanel
            top={noticeShown ? 380 : 345}
            sections={[
              { title: BRIEF_PURPOSE_TITLE, paragraphs: [BRIEF_PURPOSE_BODY] },
              {
                title: BRIEF_AUDIENCE_TITLE,
                paragraphs: [
                  <>
                    {BRIEF_AUDIENCE_BODY}
                    <span className="text-accent-hover">{BRIEF_TYPED_SUGGESTION}</span>
                  </>,
                ],
              },
            ]}
          />
        </ReviewPageChrome>
      </Page>
    </>
  );
}
